// Zebra Browser Print API
// Works with Zebra ZD421 and other Zebra printers
// Requires Zebra Browser Print app installed and running on user's local PC
//
// SECURITY: For HTTPS websites, Zebra Browser Print must be accessed via HTTPS (port 9101)
// The user must accept the self-signed certificate by visiting https://localhost:9101/available

// Always use HTTPS for security - Zebra Browser Print supports HTTPS on port 9101
const ZEBRA_HTTPS_URL = 'https://localhost:9101';
const ZEBRA_HTTP_URL = 'http://localhost:9100';

// Detect if site is served over HTTPS
const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';

// For HTTPS sites, ONLY use HTTPS (browser will block mixed content anyway)
// For HTTP sites (local dev), prefer HTTP but fall back to HTTPS
let activeZebraUrl = isHttps ? ZEBRA_HTTPS_URL : ZEBRA_HTTP_URL;

// Connection timeout in milliseconds
const CONNECTION_TIMEOUT = 5000;

export interface ZebraPrinter {
  name: string;
  uid: string;
  connection: string;
  deviceType: string;
  version: number;
  provider: string;
  manufacturer: string;
}

// Store full printer objects for printing
let cachedPrinters: ZebraPrinter[] = [];

export interface ZebraPrintStatus {
  isConnected: boolean;
  printers: ZebraPrinter[];
  needsCertificate?: boolean;
  error?: string;
}

// Helper function to fetch with timeout
async function fetchWithTimeout(url: string, options: RequestInit, timeout: number): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

// Check if Zebra Browser Print is running and get available printers
export async function checkZebraPrintStatus(): Promise<ZebraPrintStatus> {
  // For HTTPS sites, we MUST use HTTPS to avoid mixed content blocking
  // For HTTP sites (local dev), try HTTP first, then HTTPS
  const urlsToTry = isHttps
    ? [ZEBRA_HTTPS_URL]
    : [ZEBRA_HTTP_URL, ZEBRA_HTTPS_URL];

  let lastError: string | undefined;
  let certificateError = false;

  for (const url of urlsToTry) {
    try {
      console.log(`[ZebraPrint] Trying ${url}/available...`);

      const response = await fetchWithTimeout(
        `${url}/available`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
        },
        CONNECTION_TIMEOUT
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[ZebraPrint] Response data:', data);

        const printerArray = data.printer || data;

        if (Array.isArray(printerArray) && printerArray.length > 0) {
          activeZebraUrl = url;
          console.log(`[ZebraPrint] Connected via ${url}`);

          cachedPrinters = printerArray.map((printer: any) => ({
            name: printer.name || 'Unknown Printer',
            uid: printer.uid || '',
            connection: printer.connection || 'unknown',
            deviceType: printer.deviceType || 'printer',
            version: printer.version || 0,
            provider: printer.provider || '',
            manufacturer: printer.manufacturer || 'Zebra',
          }));

          console.log('[ZebraPrint] Found printers:', cachedPrinters.map(p => p.name));
          return { isConnected: true, printers: cachedPrinters };
        } else {
          console.log('[ZebraPrint] No printers in response');
          lastError = 'Zebra Browser Print is running but no printers are connected';
        }
      } else {
        console.log(`[ZebraPrint] HTTP ${response.status} from ${url}`);
        lastError = `HTTP ${response.status}`;
      }
    } catch (error: any) {
      console.log(`[ZebraPrint] Error connecting to ${url}:`, error.message || error);

      // Check for specific error types
      if (error.name === 'AbortError') {
        lastError = 'Connection timeout - is Zebra Browser Print running?';
      } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        // This typically indicates HTTPS certificate issue or service not running
        if (url === ZEBRA_HTTPS_URL) {
          certificateError = true;
          lastError = 'Certificate not accepted or service not running';
        } else {
          lastError = 'Cannot connect - is Zebra Browser Print running?';
        }
      } else {
        lastError = error.message || 'Unknown connection error';
      }
    }
  }

  // Clear cached printers on failure
  cachedPrinters = [];

  // If we're on HTTPS and failed to connect, it's likely a certificate issue
  const needsCert = isHttps && certificateError;

  console.log(`[ZebraPrint] Connection failed. HTTPS: ${isHttps}, CertError: ${certificateError}, NeedsCert: ${needsCert}`);

  return {
    isConnected: false,
    printers: [],
    needsCertificate: needsCert,
    error: lastError,
  };
}

// Get list of Zebra printers (uses cached data from checkZebraPrintStatus)
export async function getZebraPrinters(): Promise<ZebraPrinter[]> {
  if (cachedPrinters.length > 0) {
    return cachedPrinters;
  }
  const status = await checkZebraPrintStatus();
  return status.printers;
}

// Get cached printer by name
export function getCachedPrinter(name: string): ZebraPrinter | undefined {
  return cachedPrinters.find(p => p.name === name);
}

// Send ZPL to printer via Zebra Browser Print (secure HTTPS connection)
export async function printZpl(
  printerName: string,
  zplCode: string
): Promise<{ success: boolean; error?: string }> {
  try {
    console.log('[ZebraPrint] Sending ZPL to printer:', printerName);
    console.log('[ZebraPrint] ZPL length:', zplCode.length);

    // Get the full printer object from cache
    const printer = getCachedPrinter(printerName);
    if (!printer) {
      console.error('[ZebraPrint] Printer not found in cache:', printerName);
      return { success: false, error: 'Printer not found. Please click "Retry Connection" to refresh.' };
    }

    const response = await fetchWithTimeout(
      `${activeZebraUrl}/write`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device: {
            uid: printer.uid,
            name: printer.name,
            connection: printer.connection,
            deviceType: printer.deviceType,
            version: printer.version,
            provider: printer.provider,
            manufacturer: printer.manufacturer,
          },
          data: zplCode,
        }),
      },
      10000 // 10 second timeout for printing
    );

    const responseText = await response.text();

    if (!response.ok) {
      console.error('[ZebraPrint] Print failed:', response.status, responseText);
      return { success: false, error: `Print failed: HTTP ${response.status}` };
    }

    // Check for error in response
    if (responseText && responseText.toLowerCase().includes('failed')) {
      console.error('[ZebraPrint] Print error in response:', responseText);
      return { success: false, error: responseText };
    }

    console.log('[ZebraPrint] ZPL sent successfully');
    return { success: true };
  } catch (error: any) {
    console.error('[ZebraPrint] Error printing:', error);

    if (error.name === 'AbortError') {
      return { success: false, error: 'Print timeout - printer may be busy or offline' };
    }

    return { success: false, error: error.message || 'Unknown print error' };
  }
}

// Direct TCP print via backend (for network-connected printers)
export async function printZplDirect(
  printerIp: string,
  zplCode: string,
  port: number = 9100
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch('/api/v1/print/zebra', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        printerIp,
        port,
        zpl: zplCode,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      return { success: false, error: error.message || 'Print failed' };
    }

    return { success: true };
  } catch (error: any) {
    console.error('[ZebraPrint] Direct print error:', error);
    return { success: false, error: error.message || 'Unknown error' };
  }
}

// Get printer status
export async function getZebraPrinterStatus(printerName: string): Promise<string> {
  try {
    const printer = getCachedPrinter(printerName);
    if (!printer) {
      return 'Not found';
    }

    const response = await fetchWithTimeout(
      `${activeZebraUrl}/read`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device: {
            uid: printer.uid,
            name: printer.name,
            connection: printer.connection,
            deviceType: printer.deviceType,
            version: printer.version,
            provider: printer.provider,
            manufacturer: printer.manufacturer,
          },
        }),
      },
      CONNECTION_TIMEOUT
    );

    if (!response.ok) {
      return 'Unknown';
    }

    const status = await response.text();
    return status || 'Ready';
  } catch (error) {
    console.error('[ZebraPrint] Error getting status:', error);
    return 'Error';
  }
}
